import { Switch } from '@components/core-ui/form-elements/Switch';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import type { TModelNormalizerSetting } from 'app/API/custom/asr-model-settings/types';
import { getEnabledPipelineNames } from '@pages/Admin/AsrProxyConsolePage/helpers/getEnabledPipelineNames';
import { toggleEnabledPipelineName } from '@pages/Admin/AsrProxyConsolePage/helpers/toggleEnabledPipelineName';
import type { TProps } from './types/TProps';

const renderPipelineGroup = (
  sectionId: string,
  title: string,
  emptyText: string,
  entries: TModelNormalizerSetting[],
  enabledNames: string[],
  onToggle: (name: string) => void,
) => (
  <section aria-labelledby={sectionId}>
    <h3 id={sectionId} className="text-sm font-medium text-foreground">
      {title}
    </h3>
    {entries.length === 0 ? (
      <p className="mt-2 text-xs text-foreground-muted">{emptyText}</p>
    ) : (
      <div className="mt-2 grid gap-3 xl:grid-cols-2">
        {entries.map((entry) => (
          <div key={entry.name} className="rounded-md border border-border bg-page p-3">
            <Switch
              label={entry.name}
              checked={enabledNames.includes(entry.name)}
              onChange={() => onToggle(entry.name)}
            />
            <p className="mt-2 text-xs leading-4 text-foreground-muted">{entry.description}</p>
          </div>
        ))}
      </div>
    )}
  </section>
);

export const PipelineSelection = ({ settings, isLoading, errorMessage, draft, onDraftChange }: TProps) => {
  if (isLoading) {
    return (
      <p role="status" className="mt-3 text-sm text-foreground-muted">
        Loading model pipeline…
      </p>
    );
  }
  if (errorMessage) {
    return (
      <p role="alert" className="mt-3 text-sm text-danger">
        {errorMessage}
      </p>
    );
  }
  if (!settings) {
    return (
      <p className="mt-3 text-sm text-foreground-muted">No pipeline settings are available for the selected model.</p>
    );
  }

  const enabledNormalizers = getEnabledPipelineNames(settings.normalizers, draft.normalizers);
  const enabledQualityCheckers = getEnabledPipelineNames(settings.qualityCheckers, draft.qualityCheckers);
  const isOverridden = draft.normalizers !== null || draft.qualityCheckers !== null;

  return (
    <div className="mt-3 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-foreground-muted">
          {isOverridden
            ? 'Overridden for this request only. Saved model settings are not changed.'
            : 'Using the saved model settings from Defaults.'}
        </p>
        <OutlinedButton
          className="!w-auto"
          disabled={!isOverridden}
          onClick={() => onDraftChange({ normalizers: null, qualityCheckers: null })}
        >
          Use saved model settings
        </OutlinedButton>
      </div>
      {renderPipelineGroup(
        'playground-pipeline-normalizers',
        'Normalizers',
        'No normalizers are registered for this model.',
        settings.normalizers,
        enabledNormalizers,
        (name) => onDraftChange({ ...draft, normalizers: toggleEnabledPipelineName(enabledNormalizers, name) }),
      )}
      {renderPipelineGroup(
        'playground-pipeline-quality-checkers',
        'Quality checkers',
        'No quality checkers are registered for this model.',
        settings.qualityCheckers,
        enabledQualityCheckers,
        (name) => onDraftChange({ ...draft, qualityCheckers: toggleEnabledPipelineName(enabledQualityCheckers, name) }),
      )}
    </div>
  );
};
